import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { FiFileText } from "react-icons/fi";
import { FaFilePdf, FaFileExcel, FaFileWord, FaFileImage, FaFileAlt } from "react-icons/fa";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

interface FullScreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  src: string;
  type: 'image' | 'file';
  name: string;
  extension?: string;
}

const isAndroidWebView = () => {
  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isWebView = /\bwv\b/.test(ua) || /Version\/[\d.]+.*Chrome/.test(ua);

  return isAndroid && isWebView;
};

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
const PDF_EXTENSION = 'pdf';

function PdfPreview({ src }: { src: string }) {
  const [totalPaginas, setTotalPaginas] = useState(0);
  const largura = Math.min(window.innerWidth, 900);

  return (
    <div className="w-full h-full overflow-auto flex justify-center">
      <Document file={src} onLoadSuccess={({ numPages }) => setTotalPaginas(numPages)}>
        {Array.from({ length: totalPaginas }, (_, i) => (
          <Page key={i} pageNumber={i + 1} width={largura} className="mb-2" />
        ))}
      </Document>
    </div>
  );
}

export function FullScreenModal({ isOpen, onClose, src, type, name, extension }: FullScreenModalProps) {
  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const ext = extension?.toLowerCase() || name.split('.').pop()?.toLowerCase() || '';
  const isImage = IMAGE_EXTENSIONS.includes(ext);
  const isPdf = ext === PDF_EXTENSION;

  const handleShare = async () => {
    const isAndroidWV = isAndroidWebView();

    if (isAndroidWV && (window as any).ReactNativeWebView?.postMessage) {
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'SHARE_IMAGE',
          payload: { src, name },
        })
      );
      return;
    }

    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const file = new File([blob], name, { type: blob.type });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: '', text: '', files: [file] });
      } else {
        alert('Este dispositivo não suporta compartilhamento de arquivos.');
      }
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
    }
  };

  const getFileIcon = () => {
    switch (ext) {
      case 'pdf':
        return <FaFilePdf className="w-24 h-24 text-red-500 mb-4" />;
      case 'xlsx':
      case 'xls':
      case 'csv':
        return <FaFileExcel className="w-24 h-24 text-green-600 mb-4" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="w-24 h-24 text-blue-500 mb-4" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FaFileImage className="w-24 h-24 text-yellow-500 mb-4" />;
      case 'txt':
        return <FaFileAlt className="w-24 h-24 text-gray-500 mb-4" />;
      default:
        return <FiFileText className="w-24 h-24 text-gray-400 mb-4" />;
    }
  };

  const renderContent = () => {
    if (isImage) {
      return (
        <img
          src={src}
          alt={name}
          className="w-full h-full object-contain"
        />
      );
    }

    if (isPdf) {
      return <PdfPreview src={src} />;
    }

    return (
      <div className="flex flex-col items-center justify-center w-full h-full">
        {getFileIcon()}
        <p className="text-gray-500 text-lg mb-4">{name}</p>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center">
      <div className="relative sm:max-w-4xl sm:max-h-[90vh] w-full h-full bg-white sm:rounded-xl shadow-lg flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
          <span className="text-sm font-medium text-gray-700 truncate max-w-[70%]">{name}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              className="flex items-center gap-1 px-3 py-1.5 bg-primary text-secondary rounded-lg hover:bg-primary-600 transition-colors text-sm"
            >
              <Icon icon="lucide:share-2" className="w-4 h-4" />
              Compartilhar
            </button>
            {!isImage && !isPdf && (
              <button
                onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
              >
                <Icon icon="lucide:download" className="w-4 h-4" />
                Download
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 bg-gray-100 rounded-full hover:bg-gray-200 transition-all"
              aria-label="Fechar"
            >
              <Icon icon="ph:x" className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
